import React, { useRef, useState, useEffect, useMemo } from 'react'
import {
  Typography,
  Card,
  Button,
  Row,
  Col,
  Checkbox,
  message,
  Modal,
  Divider,
  Tag,
} from 'antd'
import SignaturePad from 'react-signature-canvas'
import '../../css/patient_registration.scss'
import { useParams } from 'react-router-dom'
import _ from 'lodash'
import moment from 'moment'
const { Title, Text, Link } = Typography

const ConsentFomrms = (props) => {
  const signatureRef = useRef(null)
  const [showModal, setShowModal] = useState(false)
  const [useDefaultSignature, setUseDefaultSignature] = useState(false)
  const [description, setDescription] = useState('')
  const [selectedTitle, setSelectedTitle] = useState('')

  const params = useParams()

  // Separate signature forms (Doc 03) from reference docs (04-06)
  const signatureForms = (props.consentForms || []).filter(
    (f) => f.type === 'signature' || (!f.type && f.requiresSignature !== false)
  )
  const referenceDocs = (props.consentForms || []).filter(
    (f) => f.type === 'reference'
  )
  const signatureForm = signatureForms.length > 0 ? signatureForms[0] : null

  useEffect(() => {
    window.scrollTo(0, 0)
    if (props.registerationState?.consent) {
      const { signatures } = props.registerationState?.consent
      if (signatures && signatures[0]?.signature && signatureRef.current) {
        signatureRef.current.fromDataURL(signatures[0].signature)
      }
    }
    if (props.registerationState?.checkedStates) {
      setUseDefaultSignature(props.registerationState.checkedStates[0] || false)
    }
  }, [])

  const textToBase64Italic = () => {
    const { patient } = props.registerationState
    if (!patient) return
    const { firstname, lastname } = patient
    let text = firstname + ' ' + lastname

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    const fontSize = 30
    const font = `italic ${fontSize}px Arial`
    ctx.font = font

    const textWidth = ctx.measureText(text).width
    canvas.width = textWidth + 20
    canvas.height = fontSize + 20

    ctx.font = font
    ctx.fillStyle = '#000'
    ctx.fillText(text, 10, fontSize + 10)

    return canvas.toDataURL()
  }

  const handleDefaultSignature = (e) => {
    setUseDefaultSignature(e.target.checked)
    if (!e.target.checked && signatureRef.current) {
      signatureRef.current.clear()
    } else if (signatureRef.current && e.target.checked) {
      signatureRef.current.clear()
      signatureRef.current.fromDataURL(textToBase64Italic())
    }
  }

  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear()
      setUseDefaultSignature(false)
    }
  }

  const handleReview = () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      message.error('Please provide your signature before continuing.')
      return false
    }

    if (!signatureForm) {
      message.error('No consent form available.')
      return false
    }

    const signatures = [
      {
        signature: signatureRef.current.toDataURL(),
        formid: signatureForm._id,
        sequence_no: signatureForm.sequence,
      },
    ]

    props.setConsentState({ signatures }, [useDefaultSignature])
    props.navigateNext()
  }

  const handleBack = () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      const signatures = [
        {
          signature: signatureRef.current.toDataURL(),
          formid: signatureForm?._id,
          sequence_no: signatureForm?.sequence,
        },
      ]
      props.setConsentState({ signatures }, [useDefaultSignature])
    }
    props.navigateBack()
  }

  const getIframeSrc = (htmlContent) => {
    const blob = new Blob([htmlContent], { type: 'text/html' })
    return URL.createObjectURL(blob)
  }

  const formatEffectiveDate = (dateStr) => {
    if (!dateStr) return ''
    return moment(dateStr).format('MM/DD/YYYY')
  }

  const renderConsentContent = () => {
    if (!signatureForm) return null
    return (
      <div style={{ marginBottom: 16 }}>
        <Text strong>{signatureForm.title}</Text>
        {signatureForm.version && (
          <Tag color='blue' style={{ marginLeft: 8 }}>v{signatureForm.version}</Tag>
        )}
        <p style={{ margin: '8px 0' }}>
          {signatureForm.description}
          <Button
            type='link'
            className='link_button'
            onClick={() => {
              setShowModal(true)
              setDescription(signatureForm.view)
              setSelectedTitle(signatureForm.title)
            }}
          >
            View Full Document
          </Button>
        </p>
      </div>
    )
  }

  const renderReferenceDocs = () => {
    if (_.isEmpty(referenceDocs)) return null
    return (
      <div style={{
        background: '#f6f8fa',
        border: '1px solid #d9e2ec',
        borderRadius: 6,
        padding: '12px 16px',
        marginBottom: 16,
      }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          Related Legal Documents
        </Text>
        {referenceDocs.map((doc, index) => (
          <div key={index} style={{
            padding: '6px 0',
            borderBottom: index < referenceDocs.length - 1 ? '1px solid #e8edf2' : 'none',
          }}>
            {doc.url ? (
              <Link href={doc.url} target='_blank'>
                {doc.title}
              </Link>
            ) : (
              <Text>{doc.title}</Text>
            )}
            {doc.version && doc.effective_date && (
              <Tag color='default' style={{ marginLeft: 8, fontSize: 11 }}>
                v{doc.version} - Effective {formatEffectiveDate(doc.effective_date)}
              </Tag>
            )}
          </div>
        ))}
        <Text type='secondary' style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
          By signing the Participation Consent, the patient acknowledges these documents.
        </Text>
      </div>
    )
  }

  const renderSignatureSection = () => {
    return (
      <>
        <div className='sigText'>
          <span style={{ color: 'red', paddingRight: 4 }}>*</span>Electronic Signature
        </div>
        <Row gutter={25} style={{ marginBottom: 20 }}>
          <Col md={18}>
            <Row>
              <Col md={24} xs={24}>
                <SignaturePad
                  ref={signatureRef}
                  canvasProps={{
                    className: 'signatureCanvas',
                  }}
                  clearOnResize={false}
                />
              </Col>
              <Col md={24} xs={24}>
                <Button type='primary' onClick={clearSignature}>
                  Clear
                </Button>
              </Col>
            </Row>
          </Col>
          <Col md={6}>
            <Row>
              <Col md={24} xs={24}>
                <Checkbox
                  onChange={handleDefaultSignature}
                  checked={useDefaultSignature}
                >
                  Use Default Signature
                </Checkbox>
              </Col>
            </Row>
          </Col>
        </Row>
      </>
    )
  }

  const renderModal = () => {
    return (
      <Modal
        title={selectedTitle}
        className={'formModal consent-modal'}
        destroyOnClose={true}
        footer={false}
        visible={showModal}
        width={'80%'}
        closable={true}
        onCancel={() => {
          setDescription('')
          setSelectedTitle('')
          setShowModal(false)
        }}
      >
        <iframe
          src={getIframeSrc(description)}
          style={{
            width: '100%',
            height: '85vh',
            border: 'none',
          }}
          title='Consent Form'
        />
      </Modal>
    )
  }

  return (
    <>
      <Card bordered={false}>
        <Title level={4}>PARTICIPATION CONSENT</Title>
        <Title level={5}>
          Please review the consent document and provide the patient's electronic signature.
        </Title>

        {renderConsentContent()}
        <Divider />
        {renderReferenceDocs()}
        {renderSignatureSection()}
        {renderModal()}

        <Row justify='end' gutter={25}>
          <Col xs={12} md={6}>
            <Button
              type='secondary'
              onClick={handleBack}
              className='form-button'
            >
              Back
            </Button>
          </Col>
          <Col xs={12} md={6}>
            <Button
              type='primary'
              onClick={handleReview}
              className='form-button'
            >
              Review
            </Button>
          </Col>
        </Row>
      </Card>
    </>
  )
}

export default ConsentFomrms
